import { getMic } from "@/lib/mic";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { playUrl, stopAudio } from "@/lib/sharedAudio";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import VoiceCall from "@/components/VoiceCall";
import Mirror from "@/components/Mirror";
import TheField from "@/components/TheField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Mic, Square, Volume2, VolumeX, Loader2, Eye, ArrowLeft, Phone } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Roleplay() {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [mirror, setMirror] = useState(null);
  const [mirrorOpen, setMirrorOpen] = useState(false);
  const [loadingMirror, setLoadingMirror] = useState(false);
  const [partner, setPartner] = useState(null);
  const [intro, setIntro] = useState(false);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const bootRef = useRef(false);

  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;
    const start = scenarioId === "meet"
      ? api.post("/practice/create-partner")
      : scenarioId === "continue"
      ? api.post("/practice/return-session")
      : api.post("/practice/sessions", { scenario_id: scenarioId });
    start
      .then((r) => { setSession(r.data); setMessages(r.data.messages); if (r.data.partner) { setPartner(r.data.partner); setIntro(true); } })
      .catch((e) => { toast.error(e.response?.data?.detail || "Could not start"); navigate("/practice"); });
    return () => stopAudio();
    // eslint-disable-next-line
  }, [scenarioId]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const speak = async (text) => {
    try { const { data } = await api.post("/tts", { text, voice: "nova", session_id: session?.id, session_kind: "practice" }); await playUrl(`${BACKEND_URL}${data.url}`); } catch {}
  };

  const sendMessage = async (text) => {
    const { data } = await api.post(`/practice/sessions/${session.id}/message`, { text });
    return data.reply;
  };

  const submit = async (text) => {
    if (!text.trim() || sending || !session) return;
    setInput(""); setMessages((m) => [...m, { role: "user", text }]); setSending(true);
    try {
      const { data } = await api.post(`/practice/sessions/${session.id}/message`, { text });
      setMessages((m) => [...m, { role: "coach", text: data.reply }]);
      if (data.talk_limited) toast("Out of practice time", { duration: 8000, action: { label: "Top up", onClick: () => { window.location.href = "/topup"; } } });
      else if (voiceOn) speak(data.reply);
    }
    catch { toast.error("Try again."); } finally { setSending(false); }
  };

  const startRec = async () => {
    try {
      const stream = await getMic();
      const mr = new MediaRecorder(stream); mrRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop()); setTranscribing(true);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        try { const fd = new FormData(); fd.append("file", blob, "a.webm"); const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }); if (data.text?.trim()) await submit(data.text.trim()); }
        catch { toast.error("Couldn't hear that."); } finally { setTranscribing(false); }
      };
      mr.start(); setRecording(true);
    } catch { }
  };
  const stopRec = () => { mrRef.current?.stop(); setRecording(false); };

  const reveal = async () => {
    if (messages.filter((m) => m.role === "user").length === 0) { toast.error("Have a little conversation first."); return; }
    setLoadingMirror(true); stopAudio();
    try { const { data } = await api.post(`/practice/sessions/${session.id}/mirror`); setMirror(data); setMirrorOpen(true); }
    catch { toast.error("Could not reveal your mirror."); } finally { setLoadingMirror(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="max-w-2xl w-full mx-auto flex-1 flex flex-col px-4 sm:px-6 py-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate("/practice")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="roleplay-back"><ArrowLeft className="w-4 h-4" /> Room</button>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { stopAudio(); setCallOpen(true); }} disabled={!session} data-testid="start-call-btn"><Phone className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline max-w-[8rem] truncate">Call {partner?.name || "hands-free"}</span></Button>
            <button onClick={() => setVoiceOn((v) => !v)} data-testid="voice-toggle" className={`p-2 rounded-full transition-colors ${voiceOn ? "text-primary" : "text-muted-foreground"}`}>{voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}</button>
            <Button size="sm" className="rounded-full" onClick={reveal} disabled={loadingMirror} data-testid="reveal-mirror-btn">{loadingMirror ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Eye className="w-4 h-4 mr-1" /> End & reflect</>}</Button>
          </div>
        </div>
        {session && <div className="text-center mb-3"><span className="font-serif text-2xl">{session.scenario_title}</span></div>}

        <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="chat-messages">
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "glass rounded-bl-sm"}`} data-testid={`message-${m.role}`}>{m.text}</div>
            </motion.div>
          ))}
          {(sending || transcribing) && <div className="flex justify-start"><div className="glass rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div></div>}
        </div>

        {messages.filter((m) => m.role === "user").length >= 3 && !loadingMirror && (
          <motion.button initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onClick={reveal} data-testid="end-reflect-cta"
            className="w-full rounded-xl border border-secondary/40 bg-secondary/[0.08] px-4 py-3 mb-2 flex items-center justify-between text-left hover:bg-secondary/[0.14] transition-colors">
            <span className="text-sm"><span className="text-secondary font-medium">Done?</span> End the conversation to get your reflection &amp; tonight's work.</span>
            <Eye className="w-4 h-4 text-secondary shrink-0" />
          </motion.button>
        )}

        <form onSubmit={(e) => { e.preventDefault(); submit(input); }} className="flex items-center gap-2 pt-3 border-t border-border">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={recording ? "Listening…" : "Your reply…"} disabled={recording || transcribing} data-testid="chat-input" className="rounded-full" />
          <Button type="button" size="icon" variant={recording ? "destructive" : "outline"} className="rounded-full shrink-0" onClick={recording ? stopRec : startRec} disabled={transcribing || sending} data-testid="mic-btn">{recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</Button>
          <Button type="submit" size="icon" className="rounded-full shrink-0" disabled={!input.trim() || sending} data-testid="send-btn"><Send className="w-4 h-4" /></Button>
        </form>
      </div>

      <Mirror open={mirrorOpen} mirror={mirror} sessionId={session?.id} onClose={() => setMirrorOpen(false)} />

      {intro && partner && (
        <div className="fixed inset-0 z-[60] bg-background/97 backdrop-blur-2xl flex items-center justify-center px-6 grain" data-testid="partner-intro">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-md text-center relative z-10">
            <p className="text-xs uppercase tracking-[0.25em] text-primary mb-6">I've created someone for you to meet</p>
            <div className="flex justify-center mb-6"><TheField intensity={0.7} size={200}><span className="font-serif text-4xl">{partner.name?.[0]}</span></TheField></div>
            <h1 className="font-serif text-5xl mb-1">{partner.name}, {partner.age}</h1>
            <p className="text-primary tracking-wide mb-6">{partner.blurb}</p>
            <p className="font-serif text-xl italic text-foreground/90 leading-relaxed mb-8">{partner.why}</p>
            <Button size="lg" className="rounded-full group" onClick={() => { setIntro(false); if (voiceOn) speak(messages[0]?.text || ""); }} data-testid="meet-partner-btn">
              Meet {partner.name} <ArrowLeft className="w-4 h-4 ml-1 rotate-180 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
          </motion.div>
        </div>
      )}

      {callOpen && session && (
        <VoiceCall title={partner?.name || session.scenario_title} voice="nova"
          openingText={[...messages].reverse().find((m) => m.role === "coach")?.text || "Hello."}
          sessionKind="roleplay" autoAvatar={user?.voice_visual === "avatar"}
          canUseAvatar={false}
          sendMessage={sendMessage} onExchange={(m) => setMessages((prev) => [...prev, { role: m.role === "mirro" ? "coach" : "user", text: m.text }])}
          onEnd={() => setCallOpen(false)} />
      )}
    </div>
  );
}
